import { ref, onUnmounted, getCurrentInstance } from 'vue'
import { useVersionStore } from '@/stores/versionStore'

/**
 * Composable for periodically checking for new app versions
 * Polls version.txt and shows a non-blocking banner if update is available
 * 
 * Usage in main.ts:
 * ```typescript
 * app.use((app) => {
 *   const { startPolling } = useVersionPolling()
 *   startPolling()
 * })
 * ```
 */
export function useVersionPolling(pollIntervalMs = 30 * 1000) {
  const versionStore = useVersionStore()
  const pollingActive = ref(false)
  let pollInterval: ReturnType<typeof setInterval> | null = null

  // Get current app version from window
  // @ts-ignore - __APP_VERSION__ is injected by Vite define
  const currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'

  /**
   * Check version.txt and update store if new version detected
   */
  const checkVersion = async () => {
    try {
      const response = await fetch('/version.txt', {
        cache: 'no-store',
        headers: {
          'Pragma': 'no-cache',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      })

      if (response.ok) {
        const responseText = await response.text()
        
        // Validate response is not HTML error page
        if (!responseText.includes('<!DOCTYPE') && !responseText.includes('<html')) {
          const latestVersion = responseText.trim()
          
          if (latestVersion && latestVersion !== currentVersion) {
            console.log(`📦 New version available! Current: ${currentVersion}, Latest: ${latestVersion}`)
            versionStore.setUpdateAvailable(true, latestVersion)
          } else {
            versionStore.setUpdateAvailable(false, null)
          }
        }
      }
    } catch (error) {
      console.error('Version polling failed:', error)
      // Continue polling on error
    }
  }

  /**
   * Start periodic version polling
   */
  const startPolling = () => {
    if (pollingActive.value) {
      console.warn('Version polling already active')
      return
    }

    pollingActive.value = true

    // Check immediately on start
    checkVersion()

    // Then check periodically
    pollInterval = setInterval(() => {
      checkVersion()
    }, pollIntervalMs)
  }

  /**
   * Stop periodic version polling
   */
  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
      pollingActive.value = false
      console.log('🛑 Version polling stopped')
    }
  }

  // Auto-cleanup on component unmount (ONLY if called within a component setup)
  if (getCurrentInstance()) {
    onUnmounted(() => {
      stopPolling()
    })
  }

  return {
    startPolling,
    stopPolling,
    checkVersion,
    pollingActive
  }
}
